import {
  closeModal,
  navigate,
  openModal,
  setIsLoadingAddressesFromServer,
  setIsLoadingLocationFromServer,
  setIsLocationPicked,
  setIsUserLoggedIn,
  setLoadingLocationsSettingsFromServer,
  setLocations,
  setRestaurantLocations,
  setOrganizationFull,
  setPaymentMethods,
  setSavedAddresses,
  setIsMAInstalled,
  setIsRewardInstalled,
  setLoyaltyAccount,
  setLoyaltyRewards,
  setLoyaltyProgram,
  setLoyaltyEarningRules,
  setIsMultiLocation,
  setLockedDineInLocation,
  setLoadingPaymentMethods,
  setSiteLanguage,
  setExperiments,
  setOrderPacingLevel,
  setHasServiceFeesRules,
} from './session.actions';
import { createReducer, pushItem, removeItem } from '../common/reducerUtils';
import { EMPTY_MENU, EMPTY_RESTAURANT, EMPTY_CATALOG } from '../../core/constants';
import {
  CloseModalPayload,
  NavigatePayload,
  OpenModalPayload,
  SetIsLoadingAddressesFromServerPayload,
  SetIsLoadingLocationFromServerPayload,
  SetIsLocationPickedPayload,
  SetIsUserLoggedInPayload,
  SetLoadingLocationsSettingsFromServerPayload,
  SetLocationsPayload,
  SetPaymentMethodsPayload,
  SetSavedAddressesPayload,
  SetIsMAInstalledPayload,
  SetIsRewardInstalledPayload,
  SetLoyaltyAccountPayload,
  SetLoyaltyRewardsPayload,
  SetLoyaltyProgramPayload,
  SetLoyaltyEarningRulesPayload,
  SetIsMultiLocationPayload,
  SetLockedDineInLocationPayload,
  SetLoadingPaymentMethodsPayload,
  SetSiteLanguage,
  SetExperiments,
  SetOrderPacingLevel,
  SetRestaurantLocationsPayload,
  SetHasServiceFeesRulesPayload,
} from './session.actions.types';
import {
  Catalog,
  ExtractPayload,
  getItemsHash,
  ItemsHash,
  getCatalogItemsHash,
  CatalogItemsHash,
  Menu,
  Restaurant,
  ChargeV2,
} from '@wix/restaurants-client-logic';
import { IPaymentMethod } from '@wix/cashier-payments-widget';
import { Address } from '@wix/ambassador-addresses-web/types';
import type { PartialLocation } from '../../core/oloApi';
import { Reward } from '@wix/ambassador-loyalty-rewards/http';
import { LoyaltyAccount } from '@wix/ambassador-loyalty-accounts/types';
import { LoyaltyProgram } from '@wix/ambassador-loyalty-programs/types';
import { EarningRule } from '@wix/ambassador-loyalty-calculator/http';
import { ExperimentsBag } from '@wix/yoshi-flow-editor';
import { RestaurantLocation } from '@wix/ambassador-restaurant-locations/types';

interface SessionState {
  restaurant: Restaurant;
  menu: Menu;
  catalog: Catalog;
  charges: ChargeV2[];
  itemsHash: ItemsHash;
  catalogItemsHash: CatalogItemsHash;
  modals: OpenModalPayload[];
  routeUrlOverride?: string;
  paymentMethods: IPaymentMethod[];
  isUserLoggedIn: boolean;
  savedAddresses: Address[];
  defaultAddressId?: string;
  isLoadingAddressesFromServer: boolean;
  isLoadingLocationFromServer: boolean;
  isLoadingLocationsSettingsFromServer: boolean;
  isLocationPicked: boolean; // indicate if a user picked a location to deliver/pickup from or not.
  locations: PartialLocation[];
  loggedInUserEmail?: string;
  isMAInstalled?: boolean;
  isRewardInstalled?: boolean;
  loyaltyRewards?: Reward[];
  loyaltyAccount?: LoyaltyAccount;
  loyaltyProgram?: LoyaltyProgram;
  loyaltyEarningRules?: EarningRule[];
  isMultiLocation?: boolean;
  lockedDineInLocation?: string;
  isLoadingPaymentMethods?: boolean;
  siteLanguage?: string;
  experiments?: ExperimentsBag;
  restaurantLocations: RestaurantLocation[];
  hasServiceFeesRules?: boolean;
}

const initialState: SessionState = {
  restaurant: EMPTY_RESTAURANT,
  menu: EMPTY_MENU,
  catalog: EMPTY_CATALOG,
  charges: [],
  itemsHash: {},
  catalogItemsHash: {},
  modals: [],
  paymentMethods: [],
  isUserLoggedIn: false,
  savedAddresses: [],
  isLoadingAddressesFromServer: false,
  isLoadingLocationFromServer: false,
  isLoadingLocationsSettingsFromServer: false,
  isLocationPicked: false,
  locations: [],
  isMAInstalled: false,
  isRewardInstalled: false,
  loyaltyRewards: [],
  loyaltyAccount: {},
  loyaltyEarningRules: [],
  isMultiLocation: false,
  isLoadingPaymentMethods: true,
  siteLanguage: undefined,
  experiments: undefined,
  restaurantLocations: [],
  hasServiceFeesRules: false,
};

const reducer = createReducer<SessionState>(initialState, {
  [setOrganizationFull.toString()]: (state, payload: ExtractPayload<typeof setOrganizationFull>) => {
    const { restaurant, menu, catalog } = payload.organizationFull;
    return {
      ...state,
      restaurant,
      menu,
      catalog,
      charges: menu.chargesV2,
      itemsHash: getItemsHash(menu),
      catalogItemsHash: getCatalogItemsHash(catalog.items),
    };
  },
  [openModal.toString()]: (state, payload: OpenModalPayload) => {
    if (state.modals.find(({ modal }) => modal === payload.modal)) {
      console.warn('attempt to open the same modal twice ', payload.modal);
      return state;
    }
    return {
      ...state,
      modals: pushItem(state.modals, payload),
    };
  },
  [closeModal.toString()]: (state, payload: CloseModalPayload) => {
    return {
      ...state,
      modals: removeItem(
        state.modals,
        state.modals.findIndex(({ modal }) => modal === payload.modal),
      ),
      routeUrlOverride: undefined,
    };
  },
  [navigate.toString()]: (state, payload: NavigatePayload) => {
    return {
      ...state,
      routeUrlOverride: payload.routeUrl,
    };
  },
  [setPaymentMethods.toString()]: (state, payload: SetPaymentMethodsPayload) => {
    return {
      ...state,
      paymentMethods: payload.paymentMethods,
    };
  },
  [setIsUserLoggedIn.toString()]: (state, payload: SetIsUserLoggedInPayload) => {
    return {
      ...state,
      isUserLoggedIn: payload.isLoggedIn,
      loggedInUserEmail: payload.isLoggedIn ? payload.loggedInUserEmail : undefined,
    };
  },
  [setSavedAddresses.toString()]: (state, payload: SetSavedAddressesPayload) => {
    return {
      ...state,
      savedAddresses: payload.addresses,
      defaultAddressId: payload.defaultAddressId,
    };
  },
  [setIsLoadingAddressesFromServer.toString()]: (state, payload: SetIsLoadingAddressesFromServerPayload) => {
    return {
      ...state,
      isLoadingAddressesFromServer: payload.isLoadingAddressesFromServer,
    };
  },
  // [setIsDineInClosedInQRLocation.toString()]: (state, payload: SetIsDineInClosedInQRLocationPayload) => {
  //   return {
  //     ...state,
  //     isDineInClosedInQRLocation: payload.value,
  //   };
  // },
  [setIsLoadingLocationFromServer.toString()]: (state, payload: SetIsLoadingLocationFromServerPayload) => {
    return {
      ...state,
      isLoadingLocationFromServer: payload.isLoadingLocationFromServer,
    };
  },
  [setLoadingLocationsSettingsFromServer.toString()]: (
    state,
    payload: SetLoadingLocationsSettingsFromServerPayload,
  ) => {
    return {
      ...state,
      isLoadingLocationsSettingsFromServer: payload.isLoadingLocationsSettingsFromServer,
    };
  },
  [setLocations.toString()]: (state, payload: SetLocationsPayload) => {
    return {
      ...state,
      locations: payload.locations,
    };
  },
  [setRestaurantLocations.toString()]: (state, payload: SetRestaurantLocationsPayload) => {
    return {
      ...state,
      restaurantLocations: payload.restaurantLocations,
    };
  },
  [setHasServiceFeesRules.toString()]: (state, payload: SetHasServiceFeesRulesPayload) => {
    return {
      ...state,
      hasServiceFeesRules: payload.hasServiceFeesRules,
    };
  },
  [setIsMAInstalled.toString()]: (state, payload: SetIsMAInstalledPayload) => {
    return {
      ...state,
      isMAInstalled: payload.isMAInstalled,
    };
  },
  [setIsRewardInstalled.toString()]: (state, payload: SetIsRewardInstalledPayload) => {
    return {
      ...state,
      isRewardInstalled: payload.isRewardInstalled,
    };
  },
  [setIsLocationPicked.toString()]: (state, payload: SetIsLocationPickedPayload) => {
    return {
      ...state,
      isLocationPicked: payload.value,
    };
  },
  [setLoyaltyAccount.toString()]: (state, payload: SetLoyaltyAccountPayload) => {
    return {
      ...state,
      loyaltyAccount: payload.loyaltyAccount,
    };
  },
  [setLoyaltyRewards.toString()]: (state, payload: SetLoyaltyRewardsPayload) => {
    return {
      ...state,
      loyaltyRewards: payload.loyaltyRewards,
    };
  },
  [setLoyaltyProgram.toString()]: (state, payload: SetLoyaltyProgramPayload) => {
    return {
      ...state,
      loyaltyProgram: payload.loyaltyProgram,
    };
  },
  [setLoyaltyEarningRules.toString()]: (state, payload: SetLoyaltyEarningRulesPayload) => {
    return {
      ...state,
      loyaltyEarningRules: payload.earningRules,
    };
  },
  [setIsMultiLocation.toString()]: (state, payload: SetIsMultiLocationPayload) => {
    return {
      ...state,
      isMultiLocation: payload.value,
    };
  },
  [setLoadingPaymentMethods.toString()]: (state, payload: SetLoadingPaymentMethodsPayload) => {
    return {
      ...state,
      isLoadingPaymentMethods: payload.loading,
    };
  },
  [setLockedDineInLocation.toString()]: (state, payload: SetLockedDineInLocationPayload) => {
    return {
      ...state,
      lockedDineInLocation: payload.locationId,
    };
  },
  [setSiteLanguage.toString()]: (state, payload: SetSiteLanguage) => {
    return {
      ...state,
      siteLanguage: payload.siteLanguage,
    };
  },
  [setExperiments.toString()]: (state, payload: SetExperiments) => {
    return {
      ...state,
      experiments: payload.experiments,
    };
  },
  [setOrderPacingLevel.toString()]: (state, payload: SetOrderPacingLevel) => {
    return {
      ...state,
      orderPacing: { level: payload.value },
    };
  },
});

export default reducer;
